/**
 * Asks the API which record a reference names: `GET /planning/:project_id/refs/:ref` (ans-backend #332).
 * Only the project in the call is searched. A 404 and a clash are answers; anything else (offline, 5xx, the endpoint
 * not deployed on a legacy-shaped database) is `error`, which callers must not cache as "no such record".
 */
import { apiGet, ApiError } from './PlanningApiClient';

export interface ResolvedCandidate { kind: string; id: string; ref: string | null }

export type ResolveOutcome =
  | { status: 'found'; kind: string; id: string; ref: string | null }
  | { status: 'notFound' }
  | { status: 'ambiguous'; candidates: ResolvedCandidate[] }
  | { status: 'error' };

interface ResolveBody { kind?: unknown; id?: unknown; ref?: unknown }

const isText = (v: unknown): v is string => typeof v === 'string' && v !== '';

export async function resolveRecordRef(projectId: string, segment: string): Promise<ResolveOutcome> {
  try {
    const body = await apiGet<ResolveBody>(`/planning/${projectId}/refs/${encodeURIComponent(segment)}`);
    if (!isText(body?.id) || !isText(body?.kind)) return { status: 'error' };
    return { status: 'found', kind: body.kind, id: body.id, ref: isText(body.ref) ? body.ref : null };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 404) return { status: 'notFound' };
      if (err.status === 409) {
        const raw = (err.body as { candidates?: unknown } | null | undefined)?.candidates;
        const candidates = Array.isArray(raw)
          ? raw.filter((c): c is ResolvedCandidate => isText((c as ResolvedCandidate)?.id) && isText((c as ResolvedCandidate)?.kind))
              .map(c => ({ kind: c.kind, id: c.id, ref: isText(c.ref) ? c.ref : null }))
          : [];
        return candidates.length > 1 ? { status: 'ambiguous', candidates } : { status: 'error' };
      }
    }
    return { status: 'error' };
  }
}
